import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { ChannelManager } from 'app/typings/channelManagers';
import { RateMode } from 'app/typings/rateModes';
import { formatDateWithoutHour } from 'app/utils/dates';
import { eachDayOfInterval } from 'app/utils/dayjs';
import { computeCrossRate, getDiscountError } from 'app/utils/price';

import { RowTitleCell } from '../_components/Cells/_components/RowTitleCell';
import type { TimePeriod } from '../type';
import {
  computeCrossedPriceDescription,
  getTooltipDescription,
} from '../utils';

import './Package.scss';
import { InventoryRow } from './_components/InventoryRow';
import { getPackageIconParameters } from './utils';

type PackageProps = {
  packageId: number;
  roomId: number;
  timePeriod: TimePeriod;
};

export const Package = ({ packageId, roomId, timePeriod }: PackageProps) => {
  const { packages, rooms, getExperiencesForPackage, isDayPackage } =
    useContext(LegacyHotelContext);
  const { currencyFormatter } = useContext(CurrencyContext);
  const { isAdmin } = useContext(UserContext);
  const { hotelInfo: hotel } = useContext(HotelContext);
  const hasChannelManager = hotel?.channelManager !== ChannelManager.NONE;

  const { data: experiencesCategories } = useExperiencesCategories();

  const pkg = packages.find((p) => p.id === packageId);
  const room = rooms.find((r) => r.id === roomId);

  const isExternallyManaged =
    hasChannelManager &&
    hotel?.rateMode === RateMode.MULTI_RATE &&
    !pkg?.autoDiscountRate;

  const allDates = eachDayOfInterval({
    start: timePeriod.startDate,
    end: timePeriod.endDate,
  });

  const experiences = getExperiencesForPackage(packageId);

  const experienceDescription = computeCrossedPriceDescription(
    packageId,
    experiences,
    currencyFormatter,
    isDayPackage(packageId),
    experiencesCategories
  );

  const price = computeCrossRate(
    packageId,
    experiences,
    formatDateWithoutHour(timePeriod.startDate),
    !!pkg?.dayPackage,
    0
  );

  return (
    <>
      {!isDayPackage(packageId) && (
        <div className="package-header">{pkg?.aliasForHotel}</div>
      )}
      <div className="table">
        <InventoryRow
          dates={allDates}
          roomId={roomId}
          fieldName={'discountPrice'}
          isEditable={!isExternallyManaged}
          packageId={packageId}
          firstColumnContent={
            <RowTitleCell
              value="Staycation rate"
              tooltipTitle={getTooltipDescription(
                'discountPrice',
                experienceDescription,
                undefined,
                pkg?.dayPackage
              )}
              withTooltip
            />
          }
          getDescriptionValue={(datePlanning) => (
            <span className="crossed">
              {currencyFormatter(
                datePlanning.packagePrices[packageId].price || 0
              )}
            </span>
          )}
          getValue={(datePlanning) =>
            currencyFormatter(
              datePlanning.packagePrices[packageId].discountPrice || 0
            )
          }
        />
        <InventoryRow
          dates={allDates}
          roomId={roomId}
          fieldName={'discount'}
          isEditable={!isExternallyManaged}
          packageId={packageId}
          getIconParameters={(datePlanning) =>
            getPackageIconParameters(hotel, pkg, room, datePlanning)
          }
          firstColumnContent={
            <RowTitleCell
              value="Discount"
              tooltipTitle={getTooltipDescription(
                'discount',
                undefined,
                currencyFormatter(price),
                pkg?.dayPackage
              )}
            />
          }
          getValue={(datePlanning) =>
            `${datePlanning.packagePrices[packageId].discount || 0} %`
          }
          getError={(datePlanning) => {
            return getDiscountError(
              -(datePlanning.packagePrices[packageId].discount || 0),
              new Date(datePlanning.date),
              !!pkg?.dayPackage,
              pkg?.minimumDiscountOverrideDates ?? [],
              pkg?.minimumDiscountOverride
            );
          }}
        />
        {isAdmin && (
          <InventoryRow
            dates={allDates}
            roomId={roomId}
            fieldName={'published'}
            isEditable
            packageId={packageId}
            firstColumnContent="Published"
            getValue={(datePlanning) =>
              datePlanning.packagePrices[packageId].published
            }
          />
        )}
      </div>
    </>
  );
};
